import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { LayoutGrid, PackagePlus, Plus, Search, UserRound } from "lucide-react";
import clsx from "clsx";
import { api } from "@/lib/apiClient";
import { useCurrentUser } from "@/lib/auth";
import { isZvtConfigured, type DeviceServiceSettings } from "@/lib/deviceService";
import { useBookingRealtimeRefresh } from "@/lib/useBookingRealtimeRefresh";
import { formatCurrency, formatGrossFromNet, grossAmountFromNet } from "@/lib/money";
import {
  PAYMENT_METHOD_LABELS,
  type PaymentMethod,
} from "@/lib/paymentMethod";
import { isGuestCheckedInOnSite } from "@/lib/guestOnSite";
import { canWriteInvoice } from "@/lib/permissions";
import { usePageHeader } from "@/layout/usePageHeader";
import { Button } from "@/components/ui/Button";
import { Alert } from "@/components/ui/Alert";
import { QuickSaleProductTiles } from "@/components/pos/QuickSaleProductTiles";
import {
  QuickSaleCartPanel,
  type QuickSaleCartLineView,
  type QuickSaleLastSaleView,
} from "@/components/pos/QuickSaleCartPanel";
import {
  QuickSaleCardPaymentModal,
  type CardPaymentPhase,
} from "@/components/pos/QuickSaleCardPaymentModal";
import { QuickSaleCashPaymentModal } from "@/components/pos/QuickSaleCashPaymentModal";
import { QuickSaleReceiptPromptModal } from "@/components/pos/QuickSaleReceiptPromptModal";
import { EanInput } from "@/components/barcode/EanInput";
import { QuickSaleCreateArticleModal } from "@/pages/pos/QuickSaleCreateArticleModal";
import { QuickSalePurchaseModal } from "@/pages/pos/QuickSalePurchaseModal";
import { QuickSaleTileConfigModal } from "@/pages/pos/QuickSaleTileConfigModal";
import {
  emptyTilePage,
  normalizeTilePages,
  tilePagesFromLegacyIds,
  type PosTilePage,
} from "@/lib/posTiles";
import {
  buildPosCartSyncPayload,
  resetPosCartSyncSignature,
  syncGuestTabletPosCart,
  type PosCartPhase,
  type PosCartSyncPayload,
} from "@/lib/syncGuestTabletPosCart";
import {
  filterOnSiteGuests,
  findExactPitchCodeMatches,
} from "@/lib/onSiteGuestSearch";
import type { Article, Booking, BookingDeparture, Pitch } from "@/types/camping";

interface TenantSettingsPos {
  pos: {
    tile_pages?: PosTilePage[];
    tile_article_ids?: (string | null)[];
  };
  device_service?: DeviceServiceSettings;
}

interface PosSaleResponse {
  invoice: { id: string; invoice_number: string; total_amount: string; currency: string };
  payment: { payment_method: string };
  pickup_receipt_printed?: boolean;
  pickup_receipt_message?: string | null;
  cash_drawer_opened?: boolean;
  cash_drawer_message?: string | null;
}

interface PendingSaleContext {
  guestId: string | null;
  pitchCode: string | null;
  lines: Array<{ article_id: string; quantity: string; pickup_date?: string }>;
  hasPickupItems: boolean;
}

interface CartLine {
  lineKey: string;
  articleId: string;
  article: Article;
  quantity: number;
  pickupDate?: string;
}

function cartLineKey(articleId: string, pickupDate?: string): string {
  return pickupDate ? `${articleId}:${pickupDate}` : articleId;
}

function highlightIndexForCart(cart: CartLine[], activeLineKey: string | null): number | undefined {
  if (cart.length === 0) return undefined;
  const found = activeLineKey ? cart.findIndex((line) => line.lineKey === activeLineKey) : -1;
  return found >= 0 ? found : cart.length - 1;
}

async function fetchNextPickupDate(articleId: string): Promise<string> {
  const res = await api.get<{ pickup_date: string }>(
    `/api/v1/pos/next-pickup-date?article_id=${encodeURIComponent(articleId)}`,
  );
  return res.pickup_date;
}

function guestInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  const first = parts[0] ?? "";
  const last = parts.length > 1 ? (parts[parts.length - 1] ?? "") : "";
  const letters = `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
  return letters || "?";
}

function guestMetaLine(guest: OnSiteGuest): string {
  return [
    guest.pitchCode ? `Platz ${guest.pitchCode}` : "Ohne Platz",
    guest.keyNumber ? `Schlüssel ${guest.keyNumber}` : null,
    guest.licensePlate,
  ]
    .filter(Boolean)
    .join(" · ");
}

interface OnSiteGuest {
  guestId: string;
  guestName: string;
  bookingId: string;
  pitchCode: string | null;
  keyNumber: string | null;
  transponderId: string | null;
  licensePlate: string | null;
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function normalizeAccessKey(value: string): string | null {
  const normalized = value.trim().replace(/[^A-Za-z0-9]+/g, "").toUpperCase();
  return normalized || null;
}

function bookingToOnSiteGuest(
  booking: Booking,
  pitchById: Map<string, Pitch>,
): OnSiteGuest {
  const pitch = booking.pitch_id ? pitchById.get(booking.pitch_id) : undefined;
  return {
    guestId: booking.guest_id,
    guestName: booking.guest_name ?? "Gast",
    bookingId: booking.id,
    pitchCode: pitch?.code ?? null,
    keyNumber: booking.key_number ?? null,
    transponderId: booking.transponder_id ?? null,
    licensePlate: booking.license_plate ?? null,
  };
}

export function QuickSalePage({ embedded = false }: { embedded?: boolean }) {
  const { setPageMeta } = usePageHeader();
  const { user } = useCurrentUser();
  const groups = useMemo(() => (user?.groups as string[] | undefined) ?? [], [user]);
  const canSell = canWriteInvoice(groups);
  const today = todayIso();

  const scanInputRef = useRef<HTMLInputElement>(null);

  const [bookings, setBookings] = useState<Booking[]>([]);
  const [pitches, setPitches] = useState<Pitch[]>([]);
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [saleMode, setSaleMode] = useState<"guest" | "walk_in">("guest");
  const [selectedGuest, setSelectedGuest] = useState<OnSiteGuest | null>(null);
  const [guestMatches, setGuestMatches] = useState<OnSiteGuest[]>([]);
  const [guestMatchIndex, setGuestMatchIndex] = useState(0);
  const [scanBuffer, setScanBuffer] = useState("");
  const [scanBusy, setScanBusy] = useState(false);

  const [cart, setCart] = useState<CartLine[]>([]);
  const [activeLineKey, setActiveLineKey] = useState<string | null>(null);
  const [paidDisplay, setPaidDisplay] = useState<PosCartSyncPayload | null>(null);
  const [deviceService, setDeviceService] = useState<DeviceServiceSettings | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [lastSale, setLastSale] = useState<PosSaleResponse | null>(null);
  const [cashModalOpen, setCashModalOpen] = useState(false);
  const [cardModalOpen, setCardModalOpen] = useState(false);
  const [receiptModalOpen, setReceiptModalOpen] = useState(false);
  const [cardPhase, setCardPhase] = useState<CardPaymentPhase>("idle");
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [pendingSale, setPendingSale] = useState<PosSaleResponse | null>(null);
  const [pendingSaleContext, setPendingSaleContext] = useState<PendingSaleContext | null>(null);
  const [printingReceipt, setPrintingReceipt] = useState(false);
  const [printError, setPrintError] = useState<string | null>(null);
  const [createArticleOpen, setCreateArticleOpen] = useState(false);
  const [purchaseOpen, setPurchaseOpen] = useState(false);
  const [tileConfigOpen, setTileConfigOpen] = useState(false);
  const [tilePages, setTilePages] = useState<PosTilePage[]>(() => [emptyTilePage()]);

  const pitchById = useMemo(() => new Map(pitches.map((p) => [p.id, p])), [pitches]);

  const articleById = useMemo(() => new Map(articles.map((a) => [a.id, a])), [articles]);

  const tilePagesWithArticles = useMemo(() => {
    return tilePages.map((page) => {
      const tiles: Article[] = [];
      for (const id of page.tile_article_ids) {
        if (!id) continue;
        const article = articleById.get(id);
        if (article) tiles.push(article);
      }
      return { label: page.label, tiles };
    });
  }, [articleById, tilePages]);

  const hasVisibleTiles = tilePagesWithArticles.some((page) => page.tiles.length > 0);

  const articlesByBarcode = useMemo(() => {
    const map = new Map<string, Article>();
    for (const article of articles) {
      const code = article.barcode?.trim();
      if (code) {
        map.set(code, article);
        const normalized = normalizeAccessKey(code);
        if (normalized) map.set(normalized, article);
      }
    }
    return map;
  }, [articles]);

  const isWalkInSale = saleMode === "walk_in";

  const onSiteGuests = useMemo(() => {
    return bookings
      .filter((b) => isGuestCheckedInOnSite(b, today))
      .map((b) => bookingToOnSiteGuest(b, pitchById))
      .sort((a, b) => {
        const pitchCmp = (a.pitchCode ?? "zzz").localeCompare(b.pitchCode ?? "zzz", "de");
        if (pitchCmp !== 0) return pitchCmp;
        return a.guestName.localeCompare(b.guestName, "de");
      });
  }, [bookings, pitchById, today]);

  const cartTotalGross = useMemo(() => {
    let sum = 0;
    for (const line of cart) {
      const net = line.article.current_price;
      if (net == null) continue;
      const grossUnit = grossAmountFromNet(net, line.article.tax_rate_percent) ?? 0;
      sum += grossUnit * line.quantity;
    }
    return sum;
  }, [cart]);

  const cartItemCount = useMemo(
    () => cart.reduce((sum, line) => sum + line.quantity, 0),
    [cart],
  );

  const cartLineViews = useMemo<QuickSaleCartLineView[]>(
    () =>
      cart.map((line) => {
        const grossUnit =
          line.article.current_price != null
            ? grossAmountFromNet(line.article.current_price, line.article.tax_rate_percent)
            : null;
        const lineTotal = grossUnit != null ? grossUnit * line.quantity : null;
        return {
          lineKey: line.lineKey,
          name: line.article.name,
          articleNumber: line.article.article_number,
          quantity: line.quantity,
          unitGrossLabel:
            line.article.current_price != null
              ? formatGrossFromNet(line.article.current_price, line.article.tax_rate_percent)
              : null,
          lineTotalLabel:
            lineTotal != null ? formatCurrency(String(lineTotal.toFixed(2))) : null,
          pickupDate: line.pickupDate,
        };
      }),
    [cart],
  );

  const customerHint = selectedGuest
    ? [selectedGuest.guestName, selectedGuest.pitchCode ? `Platz ${selectedGuest.pitchCode}` : null]
        .filter(Boolean)
        .join(" · ")
    : isWalkInSale
      ? "Laufkunde"
      : "Noch kein Gast gewählt";

  const lastSaleView = useMemo<QuickSaleLastSaleView | null>(() => {
    if (!lastSale) return null;
    const pickupNote = lastSale.pickup_receipt_printed
      ? { ok: true, text: "Abholbeleg wurde gedruckt." }
      : lastSale.pickup_receipt_message
        ? { ok: false, text: lastSale.pickup_receipt_message }
        : null;
    return {
      invoiceNumber: lastSale.invoice.invoice_number,
      amountLabel: `${formatCurrency(lastSale.invoice.total_amount)} ${lastSale.invoice.currency}`,
      paymentLabel:
        PAYMENT_METHOD_LABELS[lastSale.payment.payment_method as PaymentMethod] ??
        lastSale.payment.payment_method,
      pickupNote,
    };
  }, [lastSale]);

  const focusScan = useCallback(() => {
    window.setTimeout(() => scanInputRef.current?.focus(), 50);
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const onSiteParams = new URLSearchParams({ reference_date: today });
    Promise.all([
      api.get<Booking[]>(`/api/v1/bookings/on-site?${onSiteParams}`),
      api.get<Pitch[]>("/api/v1/pitches"),
      api.get<Article[]>("/api/v1/articles?for_quick_sale=true"),
      api.get<TenantSettingsPos>("/api/v1/settings"),
    ])
      .then(([onSite, p, a, settings]) => {
        setBookings(onSite);
        setPitches(p);
        setArticles(a.filter((row) => row.is_active));
        const pos = settings.pos;
        if (pos?.tile_pages?.length) {
          setTilePages(normalizeTilePages(pos.tile_pages));
        } else {
          setTilePages(tilePagesFromLegacyIds(pos?.tile_article_ids));
        }
        setDeviceService(settings.device_service ?? null);
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, [today]);

  useEffect(() => {
    if (!embedded) {
      setPageMeta({
        title: "Schnellverkauf",
        subtitle: "Gast oder Laufkunde, Artikel, Kasse",
      });
    }
    load();
    return () => {
      if (!embedded) setPageMeta(null);
    };
  }, [embedded, load, setPageMeta]);

  useBookingRealtimeRefresh(load, { enabled: canSell });

  useEffect(() => {
    if (!canSell) return;
    const hold = paidDisplay && cart.length === 0 ? paidDisplay : null;
    if (hold) {
      const timer = window.setTimeout(() => {
        void syncGuestTabletPosCart(hold).catch(() => {});
      }, 200);
      return () => window.clearTimeout(timer);
    }
    if (!isWalkInSale && !selectedGuest) {
      void syncGuestTabletPosCart(null).catch(() => {});
      return;
    }
    const phase: PosCartPhase = cashModalOpen ? "cash" : cardModalOpen ? "card" : "open";
    const payload = buildPosCartSyncPayload(selectedGuest, cart, {
      phase,
      walkIn: isWalkInSale,
      highlightIndex: highlightIndexForCart(cart, activeLineKey),
    });
    const timer = window.setTimeout(() => {
      void syncGuestTabletPosCart(payload).catch(() => {});
    }, 200);
    return () => window.clearTimeout(timer);
  }, [
    activeLineKey,
    canSell,
    cardModalOpen,
    cart,
    cashModalOpen,
    isWalkInSale,
    paidDisplay,
    selectedGuest,
  ]);

  useEffect(() => {
    if (!canSell) return;
    return () => {
      void syncGuestTabletPosCart(null).catch(() => {});
    };
  }, [canSell]);

  useEffect(() => {
    if (!loading) focusScan();
  }, [loading, focusScan]);

  useLayoutEffect(() => {
    if (loading || (!selectedGuest && !isWalkInSale)) return;
    scanInputRef.current?.focus();
  }, [cart, isWalkInSale, loading, selectedGuest]);

  const selectGuest = useCallback(
    (guest: OnSiteGuest) => {
      resetPosCartSyncSignature();
      setPaidDisplay(null);
      setSaleMode("guest");
      setSelectedGuest(guest);
      setScanBuffer("");
      setGuestMatches([]);
      setGuestMatchIndex(0);
      setError(null);
      focusScan();
    },
    [focusScan],
  );

  const updateGuestMatches = useCallback(
    (raw: string) => {
      const matches = filterOnSiteGuests(onSiteGuests, raw);
      setGuestMatches(matches);
      setGuestMatchIndex(0);
    },
    [onSiteGuests],
  );

  const resolveGuestFromScan = useCallback(
    async (q: string): Promise<"selected" | "choices" | "none"> => {
      const pitchMatches = findExactPitchCodeMatches(onSiteGuests, q);
      if (pitchMatches.length === 1) {
        selectGuest(pitchMatches[0]!);
        return "selected";
      }
      if (pitchMatches.length > 1) {
        setGuestMatches(pitchMatches);
        setGuestMatchIndex(0);
        return "choices";
      }

      const local = filterOnSiteGuests(onSiteGuests, q);
      if (local.length === 1) {
        selectGuest(local[0]!);
        return "selected";
      }
      if (local.length > 1) {
        setGuestMatches(local);
        setGuestMatchIndex(0);
        return "choices";
      }

      const accessNorm = normalizeAccessKey(q);
      if (accessNorm) {
        const accessMatches = await api.get<BookingDeparture[]>(
          `/api/v1/bookings/access-lookup?${new URLSearchParams({ q }).toString()}`,
        );
        const onSiteFromAccess = accessMatches
          .filter((b) => isGuestCheckedInOnSite(b, today))
          .map((b) => bookingToOnSiteGuest(b, pitchById));

        if (onSiteFromAccess.length === 1) {
          selectGuest(onSiteFromAccess[0]!);
          return "selected";
        }
        if (onSiteFromAccess.length > 1) {
          setGuestMatches(onSiteFromAccess);
          setGuestMatchIndex(0);
          return "choices";
        }
      }

      return "none";
    },
    [onSiteGuests, pitchById, selectGuest, today],
  );

  const startWalkInSale = useCallback(() => {
    setPaidDisplay(null);
    setSaleMode("walk_in");
    setSelectedGuest(null);
    setScanBuffer("");
    setGuestMatches([]);
    setGuestMatchIndex(0);
    setError(null);
    focusScan();
  }, [focusScan]);

  const backToGuestPicker = useCallback(() => {
    resetPosCartSyncSignature();
    setPaidDisplay(null);
    setSaleMode("guest");
    setSelectedGuest(null);
    setScanBuffer("");
    setGuestMatches([]);
    setGuestMatchIndex(0);
    setError(null);
    focusScan();
  }, [focusScan]);

  const clearCart = useCallback(() => {
    setPaidDisplay(null);
    setActiveLineKey(null);
    setCart([]);
    focusScan();
  }, [focusScan]);

  const addArticleToCart = useCallback(
    async (article: Article) => {
      let pickupDate: string | undefined;
      if (article.requires_pickup_receipt) {
        try {
          pickupDate = await fetchNextPickupDate(article.id);
        } catch {
          pickupDate = todayIso();
        }
      }
      const lineKey = cartLineKey(article.id, pickupDate);
      setActiveLineKey(lineKey);
      setPaidDisplay(null);
      setCart((prev) => {
        const idx = prev.findIndex((line) => line.lineKey === lineKey);
        if (idx >= 0) {
          const next = [...prev];
          const line = next[idx]!;
          next[idx] = { ...line, quantity: line.quantity + 1 };
          return next;
        }
        return [
          ...prev,
          { lineKey, articleId: article.id, article, quantity: 1, pickupDate },
        ];
      });
      setError(null);
      focusScan();
    },
    [focusScan],
  );

  const resolveArticleFromScan = useCallback(
    async (code: string): Promise<boolean> => {
      let article =
        articlesByBarcode.get(code) ??
        articlesByBarcode.get(normalizeAccessKey(code) ?? "") ??
        null;

      if (!article) {
        try {
          article = await api.get<Article>(
            `/api/v1/articles/by-barcode/${encodeURIComponent(code)}`,
          );
          setArticles((prev) => {
            if (prev.some((a) => a.id === article!.id)) return prev;
            return [...prev, article!].sort((a, b) =>
              a.article_number.localeCompare(b.article_number),
            );
          });
        } catch {
          return false;
        }
      }

      addArticleToCart(article);
      return true;
    },
    [addArticleToCart, articlesByBarcode],
  );

  const handleScanSubmit = useCallback(async (scannedCode?: string) => {
    const code = (scannedCode ?? scanBuffer).trim();
    if (!code || scanBusy) return;

    setScanBusy(true);
    setError(null);
    try {
      if (!selectedGuest && !isWalkInSale) {
        const result = await resolveGuestFromScan(code);
        if (result === "none") {
          setError("Kein eingecheckter Gast am Platz – Platz, Schlüssel oder Name prüfen.");
        }
        setScanBuffer("");
        return;
      }

      const ok = await resolveArticleFromScan(code);
      if (!ok) {
        setError("Unbekannter Barcode – kein Schnellverkauf-Artikel.");
      }
      setScanBuffer("");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Scan fehlgeschlagen.");
      setScanBuffer("");
    } finally {
      setScanBusy(false);
      focusScan();
    }
  }, [
    focusScan,
    resolveArticleFromScan,
    resolveGuestFromScan,
    scanBuffer,
    scanBusy,
    selectedGuest,
    isWalkInSale,
  ]);

  const saleReady = isWalkInSale || selectedGuest != null;
  const canCompleteSale = cart.length > 0 && saleReady;
  const zvtConfigured = isZvtConfigured(deviceService);
  const useTerminal = zvtConfigured;

  const buildSalePayload = useCallback(
    (paymentMethod: PaymentMethod, terminal: boolean) => ({
      ...(selectedGuest ? { guest_id: selectedGuest.guestId } : {}),
      ...(selectedGuest?.pitchCode ? { pickup_pitch_code: selectedGuest.pitchCode } : {}),
      lines: cart.map((line) => ({
        article_id: line.articleId,
        quantity: String(line.quantity),
        ...(line.pickupDate ? { pickup_date: line.pickupDate } : {}),
      })),
      payment_method: paymentMethod,
      use_terminal: terminal,
      print_pickup_receipt: cart.some((line) => line.article.requires_pickup_receipt),
    }),
    [cart, selectedGuest],
  );

  const buildSaleContext = useCallback((): PendingSaleContext => {
    return {
      guestId: selectedGuest?.guestId ?? null,
      pitchCode: selectedGuest?.pitchCode ?? null,
      lines: cart.map((line) => ({
        article_id: line.articleId,
        quantity: String(line.quantity),
        ...(line.pickupDate ? { pickup_date: line.pickupDate } : {}),
      })),
      hasPickupItems: cart.some((line) => line.article.requires_pickup_receipt),
    };
  }, [cart, selectedGuest]);

  const completeSale = useCallback(
    (res: PosSaleResponse, context: PendingSaleContext) => {
      const paid = buildPosCartSyncPayload(selectedGuest, cart, {
        phase: "paid",
        walkIn: isWalkInSale,
        highlightIndex: highlightIndexForCart(cart, activeLineKey),
      });
      if (paid) setPaidDisplay(paid);
      setLastSale(res);
      setPendingSale(res);
      setPendingSaleContext(context);
      setCart([]);
      setScanBuffer("");
      setCashModalOpen(false);
      setCardModalOpen(false);
      setCardPhase("idle");
      setPaymentError(null);
      setReceiptModalOpen(true);
      setPrintError(null);
      focusScan();
    },
    [activeLineKey, cart, focusScan, isWalkInSale, selectedGuest],
  );

  const executeSale = useCallback(
    async (paymentMethod: PaymentMethod, terminal: boolean) => {
      if (!canSell || !canCompleteSale) return;

      setSubmitting(true);
      setPaymentError(null);
      const context = buildSaleContext();
      try {
        const res = await api.post<PosSaleResponse>(
          "/api/v1/pos/sale",
          buildSalePayload(paymentMethod, terminal),
        );
        if (paymentMethod === "card" && terminal) {
          setCardPhase("success");
        }
        completeSale(res, context);
      } catch (err) {
        const message = err instanceof Error ? err.message : "Verkauf fehlgeschlagen.";
        setPaymentError(message);
        if (paymentMethod === "card" && terminal) {
          setCardPhase("error");
        }
        focusScan();
      } finally {
        setSubmitting(false);
      }
    },
    [buildSaleContext, buildSalePayload, canCompleteSale, canSell, completeSale, focusScan],
  );

  const startCashPayment = useCallback(() => {
    if (!canCompleteSale) return;
    setPaymentError(null);
    setCashModalOpen(true);
  }, [canCompleteSale]);

  const startCardPayment = useCallback(() => {
    if (!canCompleteSale) return;
    setPaymentError(null);
    setCardPhase(useTerminal ? "processing" : "idle");
    setCardModalOpen(true);
    if (useTerminal) {
      void executeSale("card", true);
    }
  }, [canCompleteSale, executeSale, useTerminal]);

  const handleCashConfirm = useCallback(() => {
    void executeSale("cash", false);
  }, [executeSale]);

  const handleCardRetry = useCallback(() => {
    setCardPhase("processing");
    void executeSale("card", true);
  }, [executeSale]);

  const handleCardManualConfirm = useCallback(() => {
    void executeSale("card", false);
  }, [executeSale]);

  const handleSwitchToCash = useCallback(() => {
    setCardModalOpen(false);
    setCardPhase("idle");
    setPaymentError(null);
    setCashModalOpen(true);
  }, []);

  const closeReceiptModal = useCallback(() => {
    setPaidDisplay(null);
    setReceiptModalOpen(false);
    setPendingSale(null);
    setPendingSaleContext(null);
    setPrintError(null);
    focusScan();
  }, [focusScan]);

  const handlePrintReceipt = useCallback(async () => {
    if (!pendingSale) return;
    setPrintingReceipt(true);
    setPrintError(null);
    try {
      const res = await api.post<{ printed: boolean; message?: string | null }>(
        "/api/v1/pos/print-sales-receipt",
        {
          invoice_id: pendingSale.invoice.id,
          payment_method: pendingSale.payment.payment_method,
        },
      );
      if (!res.printed) {
        setPrintError(res.message ?? "Kassenbon konnte nicht gedruckt werden.");
        return;
      }
      closeReceiptModal();
    } catch (err) {
      setPrintError(err instanceof Error ? err.message : "Kassenbon konnte nicht gedruckt werden.");
    } finally {
      setPrintingReceipt(false);
    }
  }, [closeReceiptModal, pendingSale]);

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "F2") {
        e.preventDefault();
        startCashPayment();
        return;
      }
      if (e.key !== "Escape") return;
      if (
        cashModalOpen ||
        cardModalOpen ||
        receiptModalOpen ||
        createArticleOpen ||
        purchaseOpen ||
        tileConfigOpen
      ) {
        return;
      }
      if (scanBuffer.trim()) {
        e.preventDefault();
        setScanBuffer("");
        setGuestMatches([]);
        setGuestMatchIndex(0);
        return;
      }
      if (selectedGuest || isWalkInSale) {
        e.preventDefault();
        backToGuestPicker();
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [
    backToGuestPicker,
    cardModalOpen,
    cashModalOpen,
    createArticleOpen,
    isWalkInSale,
    purchaseOpen,
    receiptModalOpen,
    scanBuffer,
    selectedGuest,
    startCashPayment,
    tileConfigOpen,
  ]);

  function upsertArticle(article: Article) {
    setArticles((prev) => {
      const exists = prev.some((a) => a.id === article.id);
      if (exists) {
        return prev.map((a) => (a.id === article.id ? article : a));
      }
      return [...prev, article].sort((a, b) => a.article_number.localeCompare(b.article_number));
    });
  }

  function onArticleCreated(article: Article) {
    upsertArticle(article);
    if (selectedGuest || isWalkInSale) {
      addArticleToCart(article);
    }
    setError(null);
    focusScan();
  }

  function onPurchaseBooked(article: Article) {
    upsertArticle(article);
    setError(null);
    focusScan();
  }

  function removeCartLine(lineKey: string) {
    setActiveLineKey((current) => (current === lineKey ? null : current));
    setCart((prev) => prev.filter((line) => line.lineKey !== lineKey));
    focusScan();
  }

  function adjustCartQty(lineKey: string, delta: number) {
    setActiveLineKey(lineKey);
    setCart((prev) =>
      prev
        .map((line) =>
          line.lineKey === lineKey
            ? { ...line, quantity: Math.max(0, line.quantity + delta) }
            : line,
        )
        .filter((line) => line.quantity > 0),
    );
    focusScan();
  }

  function updateCartPickupDate(lineKey: string, pickupDate: string) {
    setCart((prev) => {
      const current = prev.find((line) => line.lineKey === lineKey);
      if (!current?.pickupDate) return prev;
      const nextKey = cartLineKey(current.articleId, pickupDate);
      if (nextKey === lineKey) return prev;
      const withoutCurrent = prev.filter((line) => line.lineKey !== lineKey);
      const existing = withoutCurrent.find((line) => line.lineKey === nextKey);
      if (existing) {
        return withoutCurrent.map((line) =>
          line.lineKey === nextKey
            ? { ...line, quantity: line.quantity + current.quantity }
            : line,
        );
      }
      return [...withoutCurrent, { ...current, lineKey: nextKey, pickupDate }];
    });
    focusScan();
  }

  const scanPlaceholder =
    selectedGuest || isWalkInSale ? "Barcode scannen oder eingeben" : "Platz, Schlüssel oder Name";

  if (!canSell) {
    return (
      <Alert variant="warning" title="Kein Zugriff">
        Schnellverkauf ist für Rezeption und Buchhaltung freigegeben.
      </Alert>
    );
  }

  const choosingGuest = !selectedGuest && !isWalkInSale;

  return (
    <div className={clsx("flex flex-col gap-4", cart.length > 0 && "max-lg:pb-32")}>
      <QuickSaleCreateArticleModal
        open={createArticleOpen}
        onClose={() => setCreateArticleOpen(false)}
        onCreated={onArticleCreated}
      />

      <QuickSalePurchaseModal
        open={purchaseOpen}
        onClose={() => setPurchaseOpen(false)}
        onBooked={onPurchaseBooked}
      />

      <QuickSaleTileConfigModal
        open={tileConfigOpen}
        onClose={() => setTileConfigOpen(false)}
        articles={articles}
        tilePages={tilePages}
        onSaved={setTilePages}
      />

      <QuickSaleCashPaymentModal
        open={cashModalOpen}
        totalGross={cartTotalGross}
        submitting={submitting}
        error={paymentError}
        onClose={() => {
          if (!submitting) {
            setCashModalOpen(false);
            setPaymentError(null);
          }
        }}
        onConfirm={handleCashConfirm}
      />

      <QuickSaleCardPaymentModal
        open={cardModalOpen}
        totalGross={cartTotalGross}
        phase={cardPhase}
        useTerminal={useTerminal}
        error={paymentError}
        onClose={() => {
          if (!submitting) {
            setCardModalOpen(false);
            setCardPhase("idle");
            setPaymentError(null);
          }
        }}
        onRetry={handleCardRetry}
        onSwitchToCash={handleSwitchToCash}
        onConfirmManual={handleCardManualConfirm}
      />

      {pendingSale && pendingSaleContext ? (
        <QuickSaleReceiptPromptModal
          open={receiptModalOpen}
          invoiceNumber={pendingSale.invoice.invoice_number}
          totalAmount={pendingSale.invoice.total_amount}
          currency={pendingSale.invoice.currency}
          paymentMethod={pendingSale.payment.payment_method as PaymentMethod}
          hasPickupItems={pendingSaleContext.hasPickupItems}
          pickupReceiptPrinted={pendingSale.pickup_receipt_printed ?? false}
          pickupReceiptMessage={pendingSale.pickup_receipt_message ?? null}
          cashDrawerOpened={pendingSale.cash_drawer_opened ?? false}
          cashDrawerMessage={pendingSale.cash_drawer_message ?? null}
          printing={printingReceipt}
          printError={printError}
          onPrint={() => void handlePrintReceipt()}
          onSkip={closeReceiptModal}
        />
      ) : null}

      {loading ? (
        <div className="grid animate-pulse gap-4 lg:grid-cols-[minmax(0,1fr)_24rem]" aria-hidden>
          <div className="space-y-4">
            <div className="h-40 rounded-2xl bg-fill-tertiary" />
            <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3">
              {Array.from({ length: 6 }, (_, index) => (
                <div key={index} className="h-28 rounded-2xl bg-fill-tertiary" />
              ))}
            </div>
          </div>
          <div className="h-96 rounded-2xl bg-fill-tertiary" />
        </div>
      ) : (
        <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_24rem]">
          <div className="min-w-0 space-y-4">
            <section
              className="rounded-2xl border border-separator bg-card p-3 shadow-ios sm:p-4"
              data-tour="pos-guest-scan"
            >
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div
                  className="eh-segmented eh-segmented--md"
                  role="tablist"
                  aria-label="Verkaufsart"
                >
                  <button
                    type="button"
                    role="tab"
                    aria-selected={!isWalkInSale}
                    className={clsx("eh-segmented-item", !isWalkInSale && "is-active")}
                    onClick={() => {
                      if (isWalkInSale) backToGuestPicker();
                    }}
                  >
                    Gast am Platz
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={isWalkInSale}
                    className={clsx("eh-segmented-item", isWalkInSale && "is-active")}
                    onClick={startWalkInSale}
                  >
                    Laufkunde
                  </button>
                </div>
                <div className="flex flex-wrap gap-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    leftIcon={<PackagePlus size={16} aria-hidden />}
                    onClick={() => setPurchaseOpen(true)}
                  >
                    Einkauf
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    leftIcon={<Plus size={16} aria-hidden />}
                    title="Neuen Artikel anlegen"
                    onClick={() => setCreateArticleOpen(true)}
                  >
                    Neuer Artikel
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    leftIcon={<LayoutGrid size={16} aria-hidden />}
                    title="Kacheln bearbeiten"
                    onClick={() => setTileConfigOpen(true)}
                  >
                    Kacheln
                  </Button>
                </div>
              </div>

              {selectedGuest ? (
                <div className="mt-3 flex items-center gap-3 rounded-xl bg-accent-soft px-3 py-2.5">
                  <span className="flex h-11 min-w-11 shrink-0 items-center justify-center rounded-xl bg-accent px-2 text-sm font-semibold text-label-on-accent">
                    {selectedGuest.pitchCode ?? guestInitials(selectedGuest.guestName)}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-label">{selectedGuest.guestName}</p>
                    <p className="truncate text-xs text-label-secondary">{guestMetaLine(selectedGuest)}</p>
                  </div>
                  <Button type="button" variant="ghost" size="sm" onClick={backToGuestPicker}>
                    Wechseln
                  </Button>
                </div>
              ) : isWalkInSale ? (
                <div className="mt-3 flex items-center gap-3 rounded-xl bg-fill-quaternary px-3 py-2.5">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-card text-label-secondary shadow-ios">
                    <UserRound size={18} aria-hidden />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-label">Laufkunde</p>
                    <p className="text-xs text-label-secondary">Kein Platz zugeordnet</p>
                  </div>
                  <Button type="button" variant="ghost" size="sm" onClick={backToGuestPicker}>
                    Gast wählen
                  </Button>
                </div>
              ) : (
                <p className="mt-3 text-sm text-label-secondary">
                  {onSiteGuests.length === 0
                    ? "Niemand ist gerade eingecheckt. Verkauf an Laufkundschaft ist möglich."
                    : `${onSiteGuests.length} ${onSiteGuests.length === 1 ? "Gast ist" : "Gäste sind"} am Platz. Platz, Schlüssel oder Name suchen.`}
                </p>
              )}

              <div className="mt-3">
                <label className="text-xs font-medium text-label-secondary" htmlFor="pos-scan-input">
                  {choosingGuest ? "Gast suchen" : "Barcode"}
                </label>
                {choosingGuest ? (
                  <div className="relative mt-1.5">
                    <Search
                      size={16}
                      aria-hidden
                      className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-label-tertiary"
                    />
                    <input
                      id="pos-scan-input"
                      ref={scanInputRef}
                      type="text"
                      className="eh-input w-full pl-10 text-base"
                      placeholder={scanPlaceholder}
                      value={scanBuffer}
                      disabled={scanBusy || onSiteGuests.length === 0}
                      autoComplete="off"
                      onChange={(e) => {
                        const value = e.target.value;
                        setScanBuffer(value);
                        updateGuestMatches(value);
                      }}
                      onKeyDown={(e) => {
                        if (guestMatches.length > 0) {
                          if (e.key === "ArrowDown") {
                            e.preventDefault();
                            setGuestMatchIndex((i) => Math.min(i + 1, guestMatches.length - 1));
                            return;
                          }
                          if (e.key === "ArrowUp") {
                            e.preventDefault();
                            setGuestMatchIndex((i) => Math.max(i - 1, 0));
                            return;
                          }
                          if (e.key === "Enter") {
                            e.preventDefault();
                            const pick = guestMatches[guestMatchIndex];
                            if (pick) selectGuest(pick);
                            return;
                          }
                        }
                        if (e.key === "Enter") {
                          e.preventDefault();
                          void handleScanSubmit();
                        }
                      }}
                    />
                  </div>
                ) : (
                  <EanInput
                    id="pos-scan-input"
                    inputRef={scanInputRef}
                    className="mt-1.5"
                    inputClassName="text-base"
                    placeholder={scanPlaceholder}
                    value={scanBuffer}
                    disabled={scanBusy}
                    showCamera
                    onChange={setScanBuffer}
                    onEnter={(code) => void handleScanSubmit(code)}
                  />
                )}
              </div>

              {choosingGuest && guestMatches.length > 0 ? (
                <ul className="mt-2 overflow-hidden rounded-xl border border-separator">
                  {guestMatches.map((guest, idx) => (
                    <li key={guest.bookingId} className="border-b border-separator last:border-b-0">
                      <button
                        type="button"
                        className={clsx(
                          "flex w-full items-center gap-3 px-3 py-2.5 text-left",
                          idx === guestMatchIndex ? "bg-accent-soft" : "hover:bg-fill-tertiary",
                        )}
                        onClick={() => selectGuest(guest)}
                      >
                        <span className="flex h-9 min-w-9 shrink-0 items-center justify-center rounded-lg bg-card px-1.5 text-xs font-semibold text-accent shadow-ios">
                          {guest.pitchCode ?? guestInitials(guest.guestName)}
                        </span>
                        <span className="min-w-0">
                          <span className="block truncate text-sm font-medium text-label">
                            {guest.guestName}
                          </span>
                          <span className="block truncate text-xs text-label-secondary">
                            {guestMetaLine(guest)}
                          </span>
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}

              {choosingGuest && scanBuffer.trim() && guestMatches.length === 0 ? (
                <p className="mt-2 text-xs text-label-secondary">
                  Kein Treffer. Enter sucht auch über Schlüssel und Transponder.
                </p>
              ) : null}

              {choosingGuest && guestMatches.length === 0 && onSiteGuests.length > 0 && scanBuffer.trim() === "" ? (
                <ul className="mt-3 grid max-h-72 gap-2 overflow-y-auto sm:grid-cols-2">
                  {onSiteGuests.map((guest) => (
                    <li key={guest.bookingId}>
                      <button
                        type="button"
                        className="flex min-h-[52px] w-full items-center gap-3 rounded-xl border border-separator bg-fill-quaternary px-3 py-2 text-left transition hover:border-accent/35 hover:bg-card"
                        onClick={() => selectGuest(guest)}
                      >
                        <span className="flex h-10 min-w-10 shrink-0 items-center justify-center rounded-lg bg-card px-1.5 text-sm font-semibold text-accent shadow-ios">
                          {guest.pitchCode ?? guestInitials(guest.guestName)}
                        </span>
                        <span className="min-w-0">
                          <span className="block truncate text-sm font-medium text-label">
                            {guest.guestName}
                          </span>
                          <span className="block truncate text-xs text-label-secondary">
                            {guestMetaLine(guest)}
                          </span>
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
            </section>

            {error ? <Alert variant="error">{error}</Alert> : null}

            <div data-tour="pos-tiles">
              {hasVisibleTiles ? (
                <QuickSaleProductTiles
                  pages={tilePagesWithArticles}
                  disabled={!saleReady}
                  disabledHint={
                    saleReady
                      ? undefined
                      : "Gast antippen oder auf Laufkunde stellen, dann sind die Artikel bereit."
                  }
                  onSelect={addArticleToCart}
                />
              ) : (
                <div className="rounded-2xl border border-dashed border-separator bg-card px-4 py-10 text-center shadow-ios">
                  <LayoutGrid className="mx-auto text-label-tertiary" size={28} aria-hidden />
                  <p className="mt-3 text-sm font-medium text-label">Keine Artikelkacheln</p>
                  <p className="mx-auto mt-1 max-w-sm text-sm text-label-secondary">
                    Häufige Artikel als Kacheln hinterlegen. Scannen funktioniert auch ohne Kacheln.
                  </p>
                  <Button
                    type="button"
                    variant="secondary"
                    className="mt-4"
                    onClick={() => setTileConfigOpen(true)}
                  >
                    Kacheln einrichten
                  </Button>
                </div>
              )}
            </div>
          </div>

          <QuickSaleCartPanel
            lines={cartLineViews}
            itemCount={cartItemCount}
            totalLabel={formatCurrency(String(cartTotalGross.toFixed(2)))}
            customerHint={customerHint}
            saleReady={saleReady}
            canPay={canCompleteSale}
            submitting={submitting}
            dockPayment={cart.length > 0}
            dockInset={embedded ? "panel" : "viewport"}
            zvtConfigured={zvtConfigured}
            today={today}
            lastSale={lastSaleView}
            onAdjustQty={adjustCartQty}
            onRemove={removeCartLine}
            onPickupDate={updateCartPickupDate}
            onClear={clearCart}
            onCash={startCashPayment}
            onCard={startCardPayment}
          />
        </div>
      )}
    </div>
  );
}
